import { z } from 'zod';

import { WorkflowStatusSchema } from './workflow-status';

// ---------------------------------------------------------------------------
// "Pull requests" — the signed-in user's pull requests across every repo
// their classic GitHub PAT can see, grouped by their relationship to each PR.
// Served by GET /api/github/pull-requests and surfaced in the work items view
// (alongside issues). Dates are ISO strings, as everywhere in contracts.
// ---------------------------------------------------------------------------

/** Merge state of a PR. GitHub GraphQL reports `state` as OPEN | CLOSED | MERGED. */
export const PullRequestStateSchema = z.enum(['open', 'closed', 'merged']);
export type PullRequestState = z.infer<typeof PullRequestStateSchema>;

/**
 * The work-items view's kind projection: issues only, PRs only, or both. Shared
 * by the dashboard filter (`work-item-filters.ts`) and the MCP `list_work_items`
 * formatter, so the URL filter and the tool's `type` input can't drift.
 */
export const WorkItemTypeSchema = z.enum(['issues', 'prs', 'all']);
export type WorkItemType = z.infer<typeof WorkItemTypeSchema>;

/**
 * A commit SHA used as a scan key: a Git object ID (40-char SHA-1 or 64-char
 * SHA-256 hex), or `""` when GitHub omits it. Constrained so a malformed value
 * can't silently poison SHA-based matching downstream.
 */
export const CommitShaSchema = z.union([
  z.literal(''),
  z
    .string()
    .regex(/^(?:[\da-f]{40}|[\da-f]{64})$/i, 'Expected a Git object ID'),
]);

/**
 * A reviewer on a PR with their terminal (latest) review state. `state` is
 * GitHub's review state (APPROVED | CHANGES_REQUESTED | COMMENTED | DISMISSED |
 * PENDING); typed `string` (not a fixed union) so an unknown value is tolerated.
 */
export const PullRequestReviewerSchema = z.object({
  login: z.string(),
  state: z.string(),
});
export type PullRequestReviewer = z.infer<typeof PullRequestReviewerSchema>;

/** A single PR row. */
export const PullRequestSummarySchema = z.object({
  number: z.number().int(),
  /** `owner/repo`, e.g. "sykeus/starter". */
  repo: z.string(),
  title: z.string(),
  url: z.url(),
  state: PullRequestStateSchema,
  isDraft: z.boolean(),
  /** Normalised pipeline stage (derived); distinct from `state`. */
  workflowStatus: WorkflowStatusSchema,
  /** ISO — when it entered `workflowStatus` (best-effort until the event log). */
  workflowStatusSince: z.iso.datetime(),
  /** Author's GitHub login, or "unknown" when GitHub omits the author. */
  author: z.string(),
  /** Assignee logins; empty when the PR has no assignees. */
  assignees: z.array(z.string()),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  /** Target/base branch (`baseRefName`); `""` when GitHub omits it. */
  baseBranch: z.string(),
  /** Source/head branch (`headRefName`); used to detect stacked chains. */
  headBranch: z.string(),
  /**
   * `owner/repo` the head branch lives in — the fork for a fork PR, otherwise
   * the same as `repo`. Paired with `headBranch` so stacking links can't form
   * across forks. `""` when GitHub omits it.
   */
  headRepo: z.string(),
  /** Head-commit SHA (`headRefOid`); the scan anchor. `""` when GitHub omits it. */
  headSha: CommitShaSchema,
  /** Base-commit SHA (`baseRefOid`); the diff base for a scan. `""` when omitted. */
  baseSha: CommitShaSchema,
  /** Lines added by the PR (GitHub `additions`); `0` when GitHub omits it. */
  additions: z.number().int().nonnegative(),
  /** Lines removed by the PR (GitHub `deletions`); `0` when GitHub omits it. */
  deletions: z.number().int().nonnegative(),
  /** Conversation comment count (GitHub `comments.totalCount`); `0` when omitted. */
  comments: z.number().int().nonnegative(),
  /** Files the PR changed (GitHub `changedFiles`); `0` when GitHub omits it. */
  changedFiles: z.number().int().nonnegative(),
  /**
   * Distinct reviewers with their terminal review state. Best-effort: the search
   * caps review events per PR, so a PR with very many reviews can undercount.
   */
  reviewers: z.array(PullRequestReviewerSchema),
  /** Same-repository issue keys this open PR explicitly closes or delivers. */
  delivers: z.array(z.string()).optional(),
  /**
   * Changed file paths (≤50) for the server-only `kruxit-area` overlap clustering.
   * Optional so rows persisted before this shipped still validate; new PRs always
   * carry it (possibly empty). The client never reads it — it reads `areaGroup`.
   */
  files: z.array(z.string()).optional(),
});
export type PullRequestSummary = z.infer<typeof PullRequestSummarySchema>;

/**
 * One bucket of results plus how many GitHub actually matched, so the UI can say
 * "showing 50 of 183" rather than a generic cap notice.
 */
export const PullRequestBucketSchema = z.object({
  items: z.array(PullRequestSummarySchema),
  /** Total matches on GitHub (may exceed `items.length` when capped). */
  totalCount: z.number().int().nonnegative(),
  /** True when `totalCount > items.length` (the list was capped). */
  truncated: z.boolean(),
});
export type PullRequestBucket = z.infer<typeof PullRequestBucketSchema>;

/** The user's relationship to a PR. Each is its own GitHub search. */
export const PullRequestBucketKeySchema = z.enum([
  'authored',
  'assigned',
  'reviewRequested',
  'reviewed',
]);
export type PullRequestBucketKey = z.infer<typeof PullRequestBucketKeySchema>;
/** The four bucket keys, in display order. */
export const PullRequestBucketKeys = PullRequestBucketKeySchema.options;

export const PullRequestsSchema = z.object({
  authored: PullRequestBucketSchema,
  assigned: PullRequestBucketSchema,
  reviewRequested: PullRequestBucketSchema,
  reviewed: PullRequestBucketSchema,
});
export type PullRequests = z.infer<typeof PullRequestsSchema>;

/** Response envelope for `GET /api/github/pull-requests`. */
export const PullRequestsResponseSchema = z.object({
  /** False when the user has not connected a GitHub PAT. */
  connected: z.boolean(),
  buckets: PullRequestsSchema,
  /** True when one or more buckets failed to load. */
  partial: z.boolean(),
  /** Bucket keys that failed to load (empty unless `partial`). */
  failedBuckets: z.array(PullRequestBucketKeySchema),
});
export type PullRequestsResponse = z.infer<typeof PullRequestsResponseSchema>;

function emptyBucket(): PullRequestBucket {
  return { items: [], totalCount: 0, truncated: false };
}

/** All four buckets empty — used for the not-connected and load-failure paths. */
export function emptyPullRequests(): PullRequests {
  return {
    authored: emptyBucket(),
    assigned: emptyBucket(),
    reviewRequested: emptyBucket(),
    reviewed: emptyBucket(),
  };
}
